import { EditIcon } from '@/components/icons/edit-icon'
import { Button, ButtonProps } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/toaster'
import { tooltipIconButtonVariants } from '@/components/ui/tooltip-icon-button'
import {
  useDeleteScheduleMutation,
  usePauseScheduleMutation,
  useUnpauseScheduleMutation,
} from '@/hooks/api/use-schedule-query'
import { cn } from '@/lib/utils'
import { DropdownMenuContentProps } from '@radix-ui/react-dropdown-menu'
import { T, useTranslate } from '@tolgee/react'
import { EllipsisVertical, PauseIcon, PlayIcon, Trash2Icon } from 'lucide-react'
import React from 'react'
import { FrequencyValue, ScheduleData } from '../schedule/_utils/types'
import { DeleteScheduleDialog } from './delete-schedule-dialog'
import { ScheduleEditDialog } from '../schedule/_components/edit-dialog'
import { DropdownItemOptionButton } from './dropdown-item-option-button'

type ScheduleOptionsMenuProps = Readonly<
  {
    id: string
    agent_id?: string | null
    tool_id?: string | null
    status?: string
    className?: string
    startDate: Date
    endDate: Date | undefined
    frequency?: FrequencyValue
    notifications?: Record<string, any> | null
    parameters?: Record<string, string>
  } & Pick<DropdownMenuContentProps, 'align' | 'alignOffset'>
>

function ScheduleOptionsMenu({
  id,
  agent_id,
  tool_id,
  status,
  className,
  startDate,
  endDate,
  frequency,
  align = 'center',
  alignOffset,
  parameters,
}: ScheduleOptionsMenuProps) {
  const { t } = useTranslate()
  const [open, setOpen] = React.useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false)

  const pauseScheduleMutation = usePauseScheduleMutation()
  const unpauseScheduleMutation = useUnpauseScheduleMutation()
  const deleteScheduleMutation = useDeleteScheduleMutation()

  const handlePauseToggle = () => {
    if (status === 'active') {
      pauseScheduleMutation.mutate(id)
    } else {
      unpauseScheduleMutation.mutate(id)
    }
  }

  const handleEdit = () => {
    setIsEditDialogOpen(true)
    setOpen(false)
  }

  const handleDelete = () => {
    setDeleteDialogOpen(true)
    setOpen(false)
  }

  const confirmDelete = () => {
    deleteScheduleMutation.mutate(id, {
      onSuccess: () => {
        setDeleteDialogOpen(false)
        toast.success(t('schedule.deleteDialog.success', { scheduleName: agent_id }))
      },
      onError: () => {
        toast.error(t('schedule.deleteDialog.error'))
      },
    })
  }

  const isPauseLoading = pauseScheduleMutation.isPending || unpauseScheduleMutation.isPending

  const scheduleData: ScheduleData = {
    id,
    agent_id: agent_id ?? '',
    tool_id: tool_id ?? '',
    startDate,
    endDate,
    time: '',
    frequency: frequency ?? 'daily',
    tool_args: parameters,
  }

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger
          className={cn(tooltipIconButtonVariants(), className)}
          title={t('schedule.optionsMenu.title')}
        >
          <EllipsisVertical className='text-neutral-150' />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          alignOffset={alignOffset}
          className='w-40 border border-white bg-neutral-25 p-1.5 shadow-sidebar'
        >
          <DropdownMenuItem asChild unstyled>
            <ScheduleOptionButton onClick={handlePauseToggle} disabled={isPauseLoading}>
              {status === 'active' ? (
                <PauseIcon className='mx-1 size-4 shrink-0 text-neutral-150' />
              ) : (
                <PlayIcon className='mx-1 size-4 shrink-0 text-neutral-150' />
              )}
              {status === 'active' ? (
                <T keyName='schedule.optionsMenu.pause' />
              ) : (
                <T keyName='schedule.optionsMenu.resume' />
              )}
            </ScheduleOptionButton>
          </DropdownMenuItem>

          <DropdownMenuItem asChild unstyled>
            <DropdownItemOptionButton onClick={handleEdit}>
              <EditIcon className='shrink-0 text-neutral-150' />
              <T keyName='schedule.optionsMenu.edit' />
            </DropdownItemOptionButton>
          </DropdownMenuItem>

          <DropdownMenuItem asChild unstyled>
            <ScheduleOptionButton onClick={handleDelete}>
              <Trash2Icon className='mx-1 size-4 shrink-0 text-error-300' />
              <p className='text-error-300'>
                <T keyName='schedule.optionsMenu.delete' />
              </p>
            </ScheduleOptionButton>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DeleteScheduleDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        scheduleName={tool_id}
        onConfirm={confirmDelete}
        isLoading={deleteScheduleMutation.isPending}
      />

      <ScheduleEditDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        scheduleData={scheduleData}
      />
    </>
  )
}

const ScheduleOptionButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        unstyled
        className={cn(
          'flex h-10 w-full select-none items-center gap-3 whitespace-nowrap rounded px-2 text-sm font-light tracking-wide outline-none hover:bg-neutral-75/75 focus:bg-neutral-75/75 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
      >
        {children}
      </Button>
    )
  },
)
ScheduleOptionButton.displayName = 'ScheduleOptionButton'

export { ScheduleOptionsMenu, type ScheduleOptionsMenuProps }
